import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { EmptyState, LoadingSpinner } from '~/components';

export const formatStatusLabel = (status: string) =>
  status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/**
 * [MCV-1850] The listing owner's "Decline" is stored as REJECTED, so the response surfaces show
 * it as "Declined". Kept apart from `formatStatusLabel`, which other screens share.
 */
export const formatResponseStatusLabel = (status: string) =>
  status === 'REJECTED' ? 'Declined' : formatStatusLabel(status);

type MarketContentPanelVariant = 'default' | 'compact' | 'scroll' | 'empty';

type MarketContentPanelProps = {
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  dataCy?: string;
  header?: ReactNode;
  variant?: MarketContentPanelVariant;
  expandAtStackedWidth?: boolean;
};

const panelPadding: Record<MarketContentPanelVariant, string> = {
  default: 'p-5 sm:p-6',
  compact: 'p-4',
  scroll: 'p-0',
  empty: 'p-6',
};

type MarketWorkspaceGridProps = {
  sidebar: ReactNode;
  detail: ReactNode;
  conversation: ReactNode;
  detailKey?: string | number;
  className?: string;
  dataCy?: string;
  sidebarClassName?: string;
  detailClassName?: string;
  expandDetailAtStackedWidth?: boolean;
  conversationClassName?: string;
  /**
   * [MCV-1889] DD22: below 1360px show one pane at a time. The list comes first; once a row is
   * opened the detail and conversation replace it, under a Back button. Left out, the stacked
   * layout this grid always had applies.
   */
  drillDown?: { detailOpen: boolean; onBack: () => void };
};

const workspaceRegionClass = {
  sidebar: 'h-[360px] sm:max-[1279px]:h-[440px] min-[1360px]:h-full',
  detail: 'h-[680px] sm:max-[1279px]:h-[760px] min-[1360px]:h-full',
  conversation: 'h-[480px] sm:max-[1279px]:h-[560px] min-[1360px]:h-full',
  // Keep these stacked expansion classes paired with the 1360px grid/region switch above.
  expandedDetail: 'max-[1359px]:!h-auto',
  expandedPanel: 'max-[1359px]:!h-auto max-[1359px]:!overflow-visible',
  expandedBody: 'max-[1359px]:!overflow-visible',
};

export const MarketWorkspaceGrid = ({
  sidebar,
  detail,
  conversation,
  detailKey,
  className,
  dataCy,
  sidebarClassName,
  detailClassName,
  expandDetailAtStackedWidth = false,
  conversationClassName,
  drillDown,
}: MarketWorkspaceGridProps) => {
  const showList = !drillDown?.detailOpen;
  return (
    <div
      data-cy={dataCy}
      data-testid={dataCy}
      className={classNames(
        'mb-8 grid grid-cols-1 items-start gap-3 min-[1360px]:mb-0 min-[1360px]:min-h-0 min-[1360px]:flex-1 min-[1360px]:auto-rows-fr min-[1360px]:items-stretch min-[1360px]:grid-cols-[minmax(280px,320px)_minmax(0,1fr)_minmax(320px,380px)] min-[1920px]:grid-cols-[minmax(320px,360px)_minmax(0,1fr)_minmax(340px,420px)]',
        // Room under the detail for the actions pinned to the bottom of the screen.
        drillDown?.detailOpen && 'max-[1359px]:pb-20',
        className
      )}
    >
      {drillDown?.detailOpen && (
        <div className="text-left min-[1360px]:hidden">
          <button
            type="button"
            data-cy="pane-back-button"
            className="inline-flex min-h-10 items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold text-primary-50 hover:bg-primary-t-900"
            onClick={drillDown.onBack}
          >
            <ArrowLeftIcon className="size-4" aria-hidden="true" />
            Back
          </button>
        </div>
      )}
      <div
        data-cy={dataCy ? `${dataCy}-sidebar` : undefined}
        data-testid={dataCy ? `${dataCy}-sidebar` : undefined}
        className={classNames(
          '@container/sidebar min-h-0 min-w-0',
          workspaceRegionClass.sidebar,
          drillDown && (showList ? 'max-[1359px]:!h-[70vh]' : 'max-[1359px]:hidden'),
          sidebarClassName
        )}
      >
        {sidebar}
      </div>
      <div
        key={detailKey}
        data-cy={dataCy ? `${dataCy}-detail` : undefined}
        data-testid={dataCy ? `${dataCy}-detail` : undefined}
        className={classNames(
          'min-h-0 min-w-0',
          workspaceRegionClass.detail,
          expandDetailAtStackedWidth && workspaceRegionClass.expandedDetail,
          drillDown && showList && 'max-[1359px]:hidden',
          detailClassName
        )}
      >
        {detail}
      </div>
      <div
        data-cy={dataCy ? `${dataCy}-conversation` : undefined}
        data-testid={dataCy ? `${dataCy}-conversation` : undefined}
        className={classNames(
          'min-h-0 min-w-0',
          workspaceRegionClass.conversation,
          drillDown && showList && 'max-[1359px]:hidden',
          conversationClassName
        )}
      >
        {conversation}
      </div>
    </div>
  );
};

export const MarketContentPanel = ({
  children,
  className,
  bodyClassName,
  dataCy,
  header,
  variant = 'default',
  expandAtStackedWidth = false,
}: MarketContentPanelProps) => (
  <section
    data-cy={dataCy}
    data-testid={dataCy}
    className={classNames(
      'flex min-w-0 flex-col rounded-lg border border-grey-900 bg-white shadow-sm',
      variant === 'scroll' && 'h-full overflow-hidden',
      variant === 'scroll' && expandAtStackedWidth && workspaceRegionClass.expandedPanel,
      className
    )}
  >
    {header && <div className="border-b border-grey-900 px-4 py-3 sm:px-5 sm:py-4">{header}</div>}
    <div
      data-cy={dataCy ? `${dataCy}-body` : undefined}
      data-testid={dataCy ? `${dataCy}-body` : undefined}
      className={classNames(
        panelPadding[variant],
        variant === 'scroll' && 'min-h-0 flex-1 overflow-auto',
        variant === 'scroll' && expandAtStackedWidth && workspaceRegionClass.expandedBody,
        variant === 'empty' && 'flex min-h-0 flex-1 items-center justify-center overflow-hidden',
        bodyClassName
      )}
    >
      {children}
    </div>
  </section>
);

export const MarketPanelHeader = ({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <div
    className={classNames(
      'flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:gap-4',
      className
    )}
  >
    <div className="min-w-0 text-left">
      <h2 className="sub-heading-sb truncate text-left text-content-primary">{title}</h2>
      {subtitle && <p className="body-text mt-1 text-left text-content-secondary">{subtitle}</p>}
    </div>
    {actions && (
      <div className="flex shrink-0 flex-wrap items-center justify-start gap-2 sm:justify-end">
        {actions}
      </div>
    )}
  </div>
);

type MarketStatus =
  | 'masked'
  | 'unmasked'
  | 'active'
  | 'inactive'
  | 'accepted'
  | 'pending'
  | 'responded'
  | 'yourTurn'
  | 'theirTurn'
  | 'eligible'
  | 'blocked'
  | 'warning'
  | 'danger'
  | 'neutral';

const pillTone: Record<MarketStatus, string> = {
  masked: 'border-warning/25 bg-warning-50 text-warning-900',
  unmasked: 'border-good/25 bg-good/10 text-good',
  active: 'border-good/25 bg-good/10 text-good',
  inactive: 'border-grey-900 bg-grey-950 text-content-secondary',
  accepted: 'border-good/25 bg-good/10 text-good',
  pending: 'border-warning/25 bg-warning-50 text-warning-900',
  responded: 'border-primary-t-700 bg-primary-t-900 text-primary-50',
  yourTurn: 'border-warning/25 bg-warning-50 text-warning-900',
  theirTurn: 'border-primary-t-800 bg-primary-t-900/60 text-primary-t-100',
  eligible: 'border-good/25 bg-good/10 text-good',
  blocked: 'border-grey-900 bg-grey-950 text-content-secondary',
  warning: 'border-warning/25 bg-warning-50 text-warning-900',
  danger: 'border-error-50/20 bg-error-t-900 text-error-50',
  neutral: 'border-grey-900 bg-white text-content-secondary',
};

export const MarketStatusPill = ({
  status,
  label,
  cyTag,
  pulse = false,
  size = 'sm',
}: {
  status: MarketStatus;
  label: string;
  cyTag?: string;
  pulse?: boolean;
  size?: 'sm' | 'md';
}) => {
  return (
    <span
      data-cy={cyTag}
      title={label}
      className={classNames(
        'inline-flex min-w-0 max-w-full items-center justify-center gap-1.5 rounded-md border px-2 py-0.5 font-semibold leading-5',
        size === 'md' ? 'text-sm' : 'overflow-hidden text-xs',
        pillTone[status]
      )}
    >
      {pulse && (
        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
      )}
      <span className={size === 'md' ? 'break-words' : 'truncate'}>{label}</span>
    </span>
  );
};

export const MarketTurnPill = ({
  turn,
  cyTag,
}: {
  turn: 'YOUR_TURN' | 'THEIR_TURN' | null | undefined;
  cyTag?: string;
}) =>
  turn ? (
    <MarketStatusPill
      status={turn === 'YOUR_TURN' ? 'yourTurn' : 'theirTurn'}
      label={turn === 'YOUR_TURN' ? 'Your Turn' : 'Their Turn'}
      cyTag={cyTag}
    />
  ) : null;

export type MarketAction = {
  label: string;
  cyTag?: string;
  disabled?: boolean;
  onClick?: () => void;
  colorVariant?: 'primary' | 'secondary' | 'tertiary' | 'translucent' | 'error';
  variant?: 'rounded' | 'square' | 'bar';
};

const actionTone: Record<NonNullable<MarketAction['colorVariant']>, string> = {
  primary: 'border-primary-50 bg-primary-50 text-white hover:bg-primary-100',
  secondary: 'border-primary-t-700 bg-white text-primary-50 hover:bg-primary-t-900',
  tertiary: 'border-grey-800 bg-white text-content-primary hover:bg-grey-950',
  translucent: 'border-primary-t-700 bg-primary-t-950 text-primary-50 hover:bg-primary-t-900',
  error: 'border-error bg-error text-white hover:bg-error-100',
};

export const MarketActionRow = ({
  actions,
  className,
  align = 'end',
  size = 'sm',
  pinned = false,
}: {
  actions: MarketAction[];
  className?: string;
  align?: 'start' | 'center' | 'end';
  size?: 'sm' | 'md';
  /** [MCV-1889] DD22: below 1360px the row sits pinned to the bottom of the screen. */
  pinned?: boolean;
}) => {
  if (actions.length === 0) return null;

  return (
    <div
      className={classNames(
        'flex flex-wrap gap-3',
        pinned &&
          'max-[1359px]:fixed max-[1359px]:inset-x-0 max-[1359px]:bottom-0 max-[1359px]:z-30 max-[1359px]:border-t max-[1359px]:border-grey-900 max-[1359px]:bg-white max-[1359px]:p-3 max-[1359px]:shadow-xl',
        align === 'start' && 'justify-start',
        align === 'center' && 'justify-center',
        align === 'end' && 'justify-end',
        className
      )}
    >
      {actions.map((action) => (
        <button
          key={`${action.cyTag ?? action.label}-${action.label}`}
          type="button"
          data-cy={action.cyTag}
          disabled={action.disabled}
          onClick={action.onClick}
          className={classNames(
            'inline-flex min-h-10 min-w-[104px] items-center justify-center rounded-md border px-3.5 py-2 font-semibold transition-colors max-[480px]:w-full max-[480px]:min-w-0',
            size === 'md' ? 'text-[1rem]' : 'text-sm',
            'disabled:cursor-not-allowed disabled:opacity-50',
            actionTone[action.colorVariant ?? 'primary'],
            action.variant === 'bar' && 'w-full min-w-[220px]',
            action.variant === 'rounded' && 'rounded-full'
          )}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
};

/**
 * The chrome of a selectable result card. `MarketSelectableCard` is a button, so a card that must
 * hold its own button (the Buying tab's "Check fit") wears these same classes on a non-button
 * container, and the two cannot drift apart.
 */
export const marketSelectableCardClass = (selected: boolean, className?: string) =>
  classNames(
    'w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-l-4 p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary-50/20',
    selected
      ? 'border-grey-900 border-l-primary-50 bg-white'
      : 'border-grey-900 border-l-transparent bg-white hover:border-l-primary-t-700 hover:bg-grey-950',
    className
  );

export const MarketSelectableCard = ({
  selected,
  children,
  className,
  dataCy,
  onClick,
}: {
  selected: boolean;
  children: ReactNode;
  className?: string;
  dataCy?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={selected}
    data-cy={dataCy}
    className={marketSelectableCardClass(selected, className)}
    onClick={onClick}
  >
    {children}
  </button>
);

export const MarketSegmentedControl = <TValue extends string>({
  options,
  value,
  onChange,
  className,
  fullWidth = false,
}: {
  options: Array<{ label: string; value: TValue; cyTag?: string }>;
  value: TValue;
  onChange: (value: TValue) => void;
  className?: string;
  fullWidth?: boolean;
}) => (
  <div
    className={classNames(
      'max-w-full gap-1 rounded-md border border-grey-900 bg-white p-0.5',
      fullWidth ? 'grid w-full' : 'inline-flex flex-wrap',
      className
    )}
    style={
      fullWidth
        ? { gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))` }
        : undefined
    }
  >
    {options.map((option) => {
      const active = option.value === value;

      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={active}
          data-cy={option.cyTag}
          className={classNames(
            'inline-flex min-h-8 min-w-0 items-center justify-center rounded px-3 py-1.5 text-center text-xs font-semibold leading-4 transition-colors',
            fullWidth ? 'w-full' : 'flex-1 whitespace-nowrap sm:flex-none',
            active ? 'bg-primary-50 text-white' : 'text-content-secondary hover:bg-grey-950'
          )}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/**
 * [MCV-1850] Declined and withdrawn responses are hidden from every response list
 * unless this is checked. A plain checkbox, not form state: toggling it refetches.
 */
export const MarketShowDeclinedToggle = ({
  checked,
  onChange,
  className,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) => (
  <label
    className={classNames(
      'flex items-center gap-2 text-left text-xs text-content-secondary',
      className
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      data-cy={CT.marketResponses.showDeclinedCheckbox}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span>Show declined &amp; withdrawn</span>
  </label>
);

export const MarketAlert = ({
  tone,
  children,
}: {
  tone: 'error' | 'warning' | 'info';
  children: ReactNode;
}) => (
  <div
    role={tone === 'info' ? 'status' : 'alert'}
    className={classNames(
      'mb-4 rounded-md border px-3 py-2 text-sm',
      tone === 'error' && 'border-error-50/20 bg-error-t-900 text-error-50',
      tone === 'warning' && 'border-warning/25 bg-warning-50 text-warning-900',
      tone === 'info' && 'border-primary-t-700 bg-primary-t-900 text-primary-50'
    )}
  >
    {children}
  </div>
);

export const MarketResponseListItem = ({
  selected,
  title,
  subtitle,
  status,
  statusLabel,
  aside,
  meta,
  timestamp,
  dataCy,
  onClick,
}: {
  selected: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  status: MarketStatus;
  statusLabel: string;
  aside?: ReactNode;
  meta?: ReactNode;
  timestamp?: ReactNode;
  dataCy?: string;
  onClick: () => void;
}) => (
  <MarketSelectableCard
    selected={selected}
    className="mb-2 shrink-0 p-3"
    dataCy={dataCy}
    onClick={onClick}
  >
    <div className="flex min-w-0 items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-sm font-semibold text-content-primary"
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </p>
        {subtitle && (
          <p
            className="mt-1 truncate text-xs text-content-secondary"
            title={typeof subtitle === 'string' ? subtitle : undefined}
          >
            {subtitle}
          </p>
        )}
      </div>
      {aside && <div className="min-w-0 max-w-[50%]">{aside}</div>}
    </div>
    {meta && (
      <div
        className="mt-2 min-w-0 truncate text-xs text-content-secondary @max-[280px]/sidebar:hidden"
        data-cy={dataCy ? `${dataCy}-meta` : undefined}
        title={typeof meta === 'string' ? meta : undefined}
      >
        {meta}
      </div>
    )}
    <div className="mt-3 flex min-w-0 items-center justify-between gap-2">
      <MarketStatusPill status={status} label={statusLabel} />
      {timestamp && (
        <span
          className="shrink-0 text-xs text-content-secondary @max-[280px]/sidebar:hidden"
          data-cy="market-response-updated-at"
        >
          {timestamp}
        </span>
      )}
    </div>
  </MarketSelectableCard>
);

export const MarketPartySummary = ({
  label,
  name,
  description,
  status,
  action,
}: {
  label: string;
  name: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="mb-5 flex flex-col items-start justify-between gap-3 border-l-4 border-primary-t-700 bg-grey-950 px-4 py-3 text-left sm:flex-row sm:items-center sm:gap-4">
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
        {label}
      </p>
      <p className="mt-1 truncate text-base font-semibold text-content-primary">{name}</p>
      {description && <p className="mt-1 text-xs text-content-secondary">{description}</p>}
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {status}
      {action}
    </div>
  </div>
);

export const IdentityGatePanel = ({
  title,
  description,
  status,
  actions = [],
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  actions?: MarketAction[];
  className?: string;
}) => (
  <div
    className={classNames(
      'border-l-4 border-primary-t-700 bg-grey-950 px-4 py-3 text-left',
      className
    )}
  >
    <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
      <div className="min-w-0">
        <p className="body-text-sb text-content-primary">{title}</p>
        {description && <p className="body-text mt-1 text-content-secondary">{description}</p>}
      </div>
      {status && <div className="shrink-0">{status}</div>}
    </div>
    {actions.length > 0 && <MarketActionRow actions={actions} align="start" className="mt-4" />}
  </div>
);

export const MarketEmptyPanel = ({
  title,
  description,
  action,
  cyTag,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  cyTag?: string;
  className?: string;
}) => (
  <div
    className={classNames(
      'flex h-full min-h-[160px] w-full flex-1 items-center justify-center text-center',
      className
    )}
  >
    <EmptyState title={title} description={description ?? ''} action={action} cyTag={cyTag} />
  </div>
);

export const MarketLoadingPanel = ({
  label = 'Loading',
  className,
}: {
  label?: string;
  className?: string;
}) => (
  <div
    aria-label={label}
    className={classNames(
      'flex h-full min-h-[160px] w-full flex-1 items-center justify-center',
      className
    )}
  >
    <LoadingSpinner />
  </div>
);

export type MarketFact = {
  label: string;
  value: ReactNode;
  cyTag?: string;
  masked?: boolean;
  highlight?: boolean;
};

export const MarketFactGrid = ({
  facts,
  className,
  density = 'default',
}: {
  facts: MarketFact[];
  className?: string;
  density?: 'default' | 'compact' | 'comfortable';
}) => (
  <dl
    className={classNames(
      density === 'compact'
        ? 'grid grid-cols-2 gap-x-3 gap-y-2'
        : density === 'comfortable'
          ? 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-x-6 gap-y-5 text-left'
          : 'grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))] sm:gap-4',
      className
    )}
  >
    {facts.map((fact) => (
      <div
        key={fact.label}
        data-cy={fact.cyTag}
        className={classNames(
          'min-w-0 border-b border-grey-900',
          density === 'compact' ? 'pb-2' : 'pb-3'
        )}
      >
        <dt
          className={classNames(
            'font-semibold text-content-secondary',
            density === 'comfortable'
              ? 'text-sm leading-5'
              : [
                  'uppercase tracking-wide',
                  density === 'compact' ? 'truncate text-[10px] leading-4' : 'text-xs',
                ]
          )}
        >
          {fact.label}
        </dt>
        <dd
          className={classNames(
            density === 'comfortable' ? 'font-normal' : 'font-medium',
            density === 'compact'
              ? 'mt-0.5 truncate text-xs'
              : density === 'comfortable'
                ? 'mt-1 text-[1rem] leading-6 [overflow-wrap:anywhere]'
                : 'mt-1 text-sm',
            fact.masked && 'text-grey-400',
            fact.highlight && !fact.masked && 'font-semibold text-primary-50',
            !fact.highlight && !fact.masked && 'text-content-primary'
          )}
        >
          {fact.value ?? '—'}
        </dd>
      </div>
    ))}
  </dl>
);

export const MarketDetailItem = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{label}</p>
    <p className="mt-1 text-sm font-semibold text-content-primary">{value}</p>
  </div>
);
